/**
 * Messaging Schemas
 *
 * Pre-built collection schemas for messaging functionality.
 * These schemas intentionally model public channels only. RecordRoom row
 * permissions cannot make a channel private by themselves; advertising DMs
 * or private groups here would expose their records to every room member.
 *
 * @example
 * ```typescript
 * import { CHANNELS_SCHEMA, MESSAGES_SCHEMA, REACTIONS_SCHEMA } from 'deepspace/worker'
 * export const schemas = [usersSchema, CHANNELS_SCHEMA, MESSAGES_SCHEMA, REACTIONS_SCHEMA]
 * ```
 */

import type { CollectionSchema } from './registry'

export const CHANNELS_SCHEMA: CollectionSchema = {
  name: 'channels',
  columns: [
    { name: 'name', storage: 'text', interpretation: 'plain' },
    { name: 'description', storage: 'text', interpretation: 'plain' },
    {
      name: 'type',
      storage: 'text',
      interpretation: { kind: 'select', options: ['public'] },
    },
    {
      name: 'createdBy',
      storage: 'text',
      interpretation: 'plain',
      userBound: true,
      immutable: true,
    },
    { name: 'archived', storage: 'number', interpretation: { kind: 'boolean' } },
  ],
  ownerField: 'createdBy',
  permissions: {
    admin: { read: true, create: true, update: true, delete: true },
    member: { read: true, create: true, update: 'own', delete: false },
    viewer: { read: true, create: false, update: false, delete: false },
  },
}

export const MESSAGES_SCHEMA: CollectionSchema = {
  name: 'messages',
  columns: [
    { name: 'channelId', storage: 'text', interpretation: 'plain' },
    { name: 'content', storage: 'text', interpretation: 'plain' },
    {
      name: 'authorId',
      storage: 'text',
      interpretation: 'plain',
      userBound: true,
      immutable: true,
    },
    { name: 'parentMessageId', storage: 'text', interpretation: 'plain' },
    { name: 'edited', storage: 'number', interpretation: { kind: 'boolean' } },
    { name: 'editedAt', storage: 'text', interpretation: { kind: 'datetime' } },
    // Soft-delete flag. Slack-style messaging keeps the row + parent IDs
    // intact (so threads remain coherent) and renders a tombstone instead.
    // Hard-delete via remove() still works if an app wants it.
    { name: 'deleted', storage: 'number', interpretation: { kind: 'boolean' } },
  ],
  ownerField: 'authorId',
  permissions: {
    admin: { read: true, create: true, update: true, delete: true },
    member: { read: true, create: true, update: 'own', delete: 'own' },
    viewer: { read: true, create: false, update: false, delete: false },
  },
}

export const REACTIONS_SCHEMA: CollectionSchema = {
  name: 'reactions',
  columns: [
    { name: 'messageId', storage: 'text', interpretation: 'plain' },
    { name: 'channelId', storage: 'text', interpretation: 'plain' },
    { name: 'emoji', storage: 'text', interpretation: 'plain' },
    { name: 'userId', storage: 'text', interpretation: 'plain', userBound: true, immutable: true },
  ],
  ownerField: 'userId',
  uniqueOn: ['messageId', 'emoji', 'userId'],
  permissions: {
    admin: { read: true, create: true, update: false, delete: true },
    member: { read: true, create: true, update: false, delete: 'own' },
    viewer: { read: true, create: false, update: false, delete: false },
  },
}

export const CHANNEL_MEMBERS_SCHEMA: CollectionSchema = {
  name: 'channel-members',
  columns: [
    { name: 'channelId', storage: 'text', interpretation: 'plain' },
    { name: 'userId', storage: 'text', interpretation: 'plain', userBound: true, immutable: true },
    { name: 'joinedAt', storage: 'text', interpretation: { kind: 'datetime' } },
  ],
  ownerField: 'userId',
  uniqueOn: ['channelId', 'userId'],
  permissions: {
    admin: { read: true, create: true, update: false, delete: true },
    member: { read: true, create: true, update: false, delete: 'own' },
    viewer: { read: true, create: false, update: false, delete: false },
  },
}

export const READ_RECEIPTS_SCHEMA: CollectionSchema = {
  name: 'read-receipts',
  columns: [
    { name: 'channelId', storage: 'text', interpretation: 'plain' },
    { name: 'userId', storage: 'text', interpretation: 'plain', userBound: true, immutable: true },
    { name: 'lastReadAt', storage: 'text', interpretation: { kind: 'datetime' } },
  ],
  ownerField: 'userId',
  uniqueOn: ['channelId', 'userId'],
  permissions: {
    admin: { read: true, create: true, update: true, delete: true },
    member: { read: true, create: true, update: 'own', delete: 'own' },
    viewer: { read: false, create: false, update: false, delete: false },
  },
}
